import filter from 'lodash/filter'
import themes, { type Theme } from 'styles/theme'

interface ProductImage {
  readonly url: string
}

export type VariantSlug =
  | 'googieSilverline'
  | 'indigoHavana'
  | 'mistiPinku'
  | 'bushBerryville'
  | 'reddRumble'
  | 'peachesCopperwire'
  | 'goldieSamba'
  | 'fetischeNoir'

export type ProductVariantId = string

export interface ProductVariant {
  readonly id: ProductVariantId
  readonly title: string
  readonly image: ProductImage | null
  readonly price: string
  readonly slug: VariantSlug
  readonly theme: Theme
  readonly flavorText: string
  readonly rideIconography: 'light' | 'dark'
  readonly muteIcon: 'light' | 'dark'
}

export interface Product {
  readonly id: string | null
  readonly slug: string
  readonly title: string
  readonly description: string | null
  readonly variants: ProductVariant[]
}

const productMixins = {
  googieSilverline: {
    flavorText:
      '10,000 prototypes, Eight years of R&D, One uncompromising standard / Healthy hydration deserved to be re-engineered',
    rideIconography: 'light',
    muteIcon: 'dark',
  },
  indigoHavana: {
    flavorText:
      'Healthy hydration begins with honest materials / Nothing inferior, nothing unnecessary, nothing left to chance',
    rideIconography: 'dark',
    muteIcon: 'light',
  },
  mistiPinku: {
    flavorText:
      'Engineered relentlessly to a principle / Durability, reliability, hygiene — zero cheap compromises',
    rideIconography: 'light',
    muteIcon: 'dark',
  },
  bushBerryville: {
    flavorText:
      'Sterility is engineered into every detail / Zero porosity, surgical precision, absolute confidence',
    rideIconography: 'dark',
    muteIcon: 'light',
  },
  reddRumble: {
    flavorText:
      'Seventy patents worldwide / Protecting a higher standard, to serve your health with hygiene',
    rideIconography: 'dark',
    muteIcon: 'light',
  },
  peachesCopperwire: {
    flavorText:
      'Engineered like a Swiss watch / Trusted like a surgical instrument, built for precision, reliability, and hygiene',
    rideIconography: 'light',
    muteIcon: 'dark',
  },
  goldieSamba: {
    flavorText:
      'Nothing touches your water by accident / Every material was chosen for health, precision, hygiene, and long-term reliability',
    rideIconography: 'light',
    muteIcon: 'dark',
  },
  fetischeNoir: {
    flavorText:
      "The future of hydration isn't just another bottle / It's a new standard of material honesty, hygiene, and performance",
    rideIconography: 'dark',
    muteIcon: 'light',
  },
} as const

const mapProductNodeToProduct = ({
  handle,
  title,
  description,
  id,
  variants,
}: Queries.ShopifyProduct): Product => {
  if (!handle) {
    throw new Error(`Expected to find handle for product ${id}`)
  }
  if (!title) {
    throw new Error(`Expected to find title for product ${id}`)
  }

  return {
    id,
    slug: handle,
    title,
    description,
    variants: variants ? mapProductVariants(variants) : [],
  }
}

const variantComparator = (a: ProductVariant, b: ProductVariant) => {
  const aIndex = themes.indexOf(a.theme)
  const bIndex = themes.indexOf(b.theme)

  if (aIndex < bIndex) {
    return -1
  }

  if (aIndex > bIndex) {
    return 1
  }

  return 0
}

const mapProductVariants = (
  variants: Queries.ShopifyProductVariants
): ProductVariant[] => {
  const productVariants: ProductVariant[] = []

  variants?.nodes?.forEach((node) => {
    if (!node) {
      return
    }

    const { id, title, image, price, theme: themeMeta } = node

    if (!id) {
      throw new Error(`Expected to find id for product variant ${id}`)
    }
    if (!title) {
      throw new Error(`Expected to find title for product variant ${id}`)
    }
    if (!price?.amount) {
      throw new Error(`Expected to find price for product variant ${id}`)
    }
    if (!themeMeta?.value) {
      throw new Error(
        `Expected to find "Marketing site theme" metafield for product variant ${id}`
      )
    }

    const slug = themeMeta.value as VariantSlug
    const theme = themes.find((t) => t.name === slug)

    if (!theme) {
      throw new Error(`Expected to find theme for slug ${slug}`)
    }

    productVariants.push({
      id,
      title,
      price: parseInt(price.amount).toString(),
      slug,
      theme,
      image:
        image && image.url
          ? {
              url: image.url,
            }
          : null,
      ...productMixins[slug],
    })
  })

  return productVariants.sort(variantComparator)
}

export const mapAllProducts = (data: Queries.allProductsQuery): Product[] => {
  if (!data?.allShopifyProduct?.nodes) {
    return []
  }

  return filter(
    data.allShopifyProduct.nodes,
    (node): node is Queries.ShopifyProduct => node != null
  ).map(mapProductNodeToProduct)
}
